import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Select } from '@lightspeed/flame/Select';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Text } from '@lightspeed/flame/Text';

import { ErrorMessageContext } from 'root/apps/InventoryCount/contexts/ErrorMessage';
import { StatusDataContext } from 'root/apps/InventoryCount/contexts/StatusData';

import { replaceSystemIdFieldString } from '../../utils/match-field-value';
import ResetImportModal from './ResetImportModal';

const MapColumn: React.FC = () => {
  const { error, setError } = React.useContext(ErrorMessageContext);
  const { isLoading, data, ValidateUploadedFile } = React.useContext(StatusDataContext);
  const [identifierFields, setIdentifierFields] = React.useState('');
  const [matchFieldIdentifier, setMatchFieldIdentifier] = React.useState('');
  const [matchFieldQty, setmatchFieldQty] = React.useState('');

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const columnWidth = '33%';

  React.useEffect(() => {
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendToValidationProcess = () => {
    ValidateUploadedFile(identifierFields, matchFieldIdentifier, matchFieldQty);
  };

  const resetState = (state: boolean) => {
    setIsModalOpen(state);
  };

  const findSelectedFieldExample = (selected: string) => {
    if (data?.previewData && data.previewData[0][selected]) {
      return data.previewData[0][selected];
    }
    return <span className="notranslate">-</span>;
  };

  return (
    <Box data-test="map-column-view" p={2} height={350}>
      {error ? (
        <Alert data-test="alert-error-map-column" type="danger" noCloseBtn mb={2}>
          {error.message}
        </Alert>
      ) : null}
      <Flex flexDirection="column" justifyContent="space-between" height={error ? '85%' : '100%'}>
        <Box>
          <Flex flexDirection="column" paddingBottom={2}>
            <Text letterSpacing={1.5} fontWeight={600} style={{ textTransform: 'uppercase' }}>
              Map columns
            </Text>
            <Text>Select fields from your file to map to fields in Lightspeed.</Text>
          </Flex>
          <Divider />
          <Box>
            <Flex>
              <Text fontWeight={600} width={columnWidth} textAlign="start" px={1} py={3}>
                Your file
              </Text>
              <Text fontWeight={600} width={columnWidth} textAlign="start" px={1} py={3}>
                Example
              </Text>
              <Text fontWeight={600} width={columnWidth} textAlign="start" px={1} py={3}>
                Lightspeed
              </Text>
            </Flex>
          </Box>
          <Divider />
          <Box>
            <Flex alignItems="center">
              <Box width={columnWidth} marginY={2} paddingRight={5}>
                <Select
                  data-test="map-column-select-matchFieldId"
                  value={matchFieldIdentifier}
                  disabled={isLoading}
                  onChange={(event) => setMatchFieldIdentifier(event.target.value)}
                >
                  <option value="" data-test="map-column-select-column-matchFieldId">
                    Select column
                  </option>
                  {data?.matchFields
                    ? data.matchFields.map((header) => {
                        if (header !== matchFieldQty && header !== 'inventory_count_row_number') {
                          return (
                            <option value={header} key={header} className="notranslate">
                              {header}
                            </option>
                          );
                        }
                        return null;
                      })
                    : null}
                </Select>
              </Box>
              <Text width={columnWidth}>
                {matchFieldIdentifier ? (
                  findSelectedFieldExample(matchFieldIdentifier)
                ) : (
                  <span className="notranslate">-</span>
                )}
              </Text>
              <Box width={columnWidth}>
                <Select
                  data-test="map-column-select-identifierFields"
                  value={identifierFields}
                  disabled={isLoading}
                  onChange={(event) => setIdentifierFields(event.target.value)}
                >
                  <option value="" data-test="map-column-select-id">
                    Select ID
                  </option>
                  {data?.identifierFields
                    ? data.identifierFields.map((header) => {
                        // return <option key={header}>{header}</option>;
                        return (
                          <option value={replaceSystemIdFieldString(header)} key={header}>
                            {replaceSystemIdFieldString(header)}
                          </option>
                        );
                      })
                    : null}
                </Select>
              </Box>
            </Flex>
            <Divider />
            <Flex alignItems="center">
              <Box width={columnWidth} marginY={2} paddingRight={5}>
                <Select
                  data-test="map-column-select-matchFieldQty"
                  value={matchFieldQty}
                  disabled={isLoading}
                  onChange={(event) => setmatchFieldQty(event.target.value)}
                >
                  <option value="" data-test="map-column-select-column-matchFieldQty">
                    Select column
                  </option>
                  {data?.matchFields
                    ? data.matchFields.map((header) => {
                        if (
                          header !== matchFieldIdentifier &&
                          header !== 'inventory_count_row_number'
                        ) {
                          return (
                            <option value={header} key={header} className="notranslate">
                              {header}
                            </option>
                          );
                        }
                        return null;
                      })
                    : null}
                </Select>
              </Box>
              <Text width={columnWidth}>
                {matchFieldQty ? (
                  findSelectedFieldExample(matchFieldQty)
                ) : (
                  <span className="notranslate">-</span>
                )}
              </Text>
              <Text width={columnWidth}>Count</Text>
            </Flex>
          </Box>
          <Divider />
        </Box>
        <Box>
          <Flex justifyContent="right">
            <Button
              data-test="map-column-restart-import-btn"
              height={30}
              p={0}
              size="small"
              variant="default"
              fill
              onClick={() => setIsModalOpen(true)}
            >
              Restart import
            </Button>
            <Button
              data-test="map-column-verify-btn"
              variant="secondary"
              size="small"
              fill
              width={90}
              height={30}
              p={0}
              ml={1}
              disabled={!(identifierFields && matchFieldIdentifier && matchFieldQty)}
              onClick={sendToValidationProcess}
            >
              {isLoading ? <Spinner /> : 'Verify file'}
            </Button>
          </Flex>
        </Box>
      </Flex>
      <ResetImportModal isModalOpen={isModalOpen} resetState={resetState} />
    </Box>
  );
};

export default MapColumn;
